/**
 * Settings → Data.
 *
 * Three screens that all do the same dangerous kind of thing — put rows
 * into a club or take them out again — and one principle shared between
 * them: nothing irreversible happens without the screen having said, in
 * words, what it is about to do and what it will leave behind.
 *
 * That is why Sample Data shows a row count next to every module before
 * you press anything, why Import will not let you apply a file it has
 * not first told you the consequences of, and why Export names the
 * modules in the file rather than handing over an unlabelled zip.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { api, ApiError, getToken } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

// =====================================================================
// Shapes
// =====================================================================

interface SampleModule {
  code: string;
  name: string;
  description: string;
  entitlement_key: string;
  sort_order: number;
  state: 'loaded' | 'available' | 'blocked';
  blocked_reason: string | null;
  loaded_at: string | null;
  row_count: number;
}

interface SampleStatus {
  modules: SampleModule[];
  total_rows: number;
  loaded_count: number;
}

interface LoadAllResult extends SampleStatus {
  loaded: { module_code: string; rows_loaded: number; skipped_reason: string | null }[];
  rows_loaded: number;
  skipped: number;
}

interface CensusRow {
  table_name: string;
  kind: 'records' | 'configuration' | 'protected';
  own_rows: number;
  sample_rows: number;
}

interface Census {
  tables: CensusRow[];
  own_records: number;
  own_configuration: number;
  sample_rows: number;
}

interface EraseResult {
  scope: 'members' | 'everything';
  applied: boolean;
  rows_in_scope: number;
  rows_deleted: number;
  tables: { table_name: string; rows_found: number; rows_deleted: number }[];
  confirmation: string | null;
}

interface ManifestTable {
  table: string;
  label: string;
  rows: number;
  importable: boolean;
}

interface ManifestModule {
  code: string;
  name: string;
  sort_order: number;
  tables: ManifestTable[];
}

interface Manifest {
  club: { id: string; slug: string; legal_name: string };
  schema_version: number;
  modules: ManifestModule[];
  total_rows: number;
}

interface TableReport {
  module: string;
  table: string;
  label: string;
  importable: boolean;
  rows: number;
  new: number;
  changed: number;
  unchanged: number;
  conflicts: { row: number; reason: string }[];
  changed_columns: string[];
  note?: string;
}

interface ImportReport {
  id: string;
  filename: string;
  uploaded_at: string;
  status: string;
  schema_version: number;
  source_club: string | null;
  can_apply: boolean;
  applied_at: string | null;
  tables: TableReport[];
  unknown_files?: string[];
  applied?: { tables: { table: string; label: string; inserted: number; updated: number }[] } | null;
}

// =====================================================================
// Bits shared by all three
// =====================================================================

function count(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * A confirmation that costs something to give.
 *
 * Not a window.confirm. Removing a club's sample data deletes thousands
 * of rows through an escape hatch in rules that otherwise never let
 * anything be deleted at all, and "are you sure" is a question people
 * answer without reading. Typing the word is the smallest thing that
 * requires actually having looked.
 */
function Confirm({
  word,
  children,
  busy,
  onConfirm,
  onCancel,
}: {
  word: string;
  children: React.ReactNode;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');

  return (
    <div className="confirm">
      <div className="confirm__body">{children}</div>
      <label className="confirm__field">
        <span>
          Type <strong>{word}</strong> to confirm
        </span>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          spellCheck={false}
        />
      </label>
      <div className="confirm__actions">
        <button
          className="danger"
          disabled={busy || typed.trim().toLowerCase() !== word.toLowerCase()}
          onClick={onConfirm}
        >
          {busy ? 'Working…' : 'Yes, do it'}
        </button>
        <button className="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Sample data
// =====================================================================

export function SampleDataPage() {
  const { atLeast } = useSession();
  const mayChange = atLeast('club_admin');

  const query = useApi<SampleStatus>('/v1/sample-data');
  const [status, setStatus] = useState<SampleStatus | null>(null);
  const [busyModule, setBusyModule] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const current = status ?? query.data;

  const load = useMutation(async (code: string) => {
    setBusyModule(code);
    try {
      const r = await api<SampleStatus & { rows_loaded: number }>(`/v1/sample-data/${code}`, {
        method: 'POST',
      });
      setStatus(r);
      setNote(`Loaded ${count(r.rows_loaded, 'row')} of ${code} sample data.`);
    } finally {
      setBusyModule(null);
    }
  });

  const remove = useMutation(async (code: string) => {
    setBusyModule(code);
    try {
      const r = await api<SampleStatus & { rows_removed: number }>(`/v1/sample-data/${code}`, {
        method: 'DELETE',
      });
      setStatus(r);
      setNote(`Removed ${count(r.rows_removed, 'row')}.`);
    } finally {
      setBusyModule(null);
    }
  });

  const loadAll = useMutation(async () => {
    const r = await api<LoadAllResult>('/v1/sample-data', { method: 'POST' });
    setStatus(r);
    // Name the reason rather than the count. "Seven modules could not be
    // loaded" next to a table that says every one of them is loaded is
    // the kind of message that makes somebody doubt the screen.
    const skipped = r.loaded.filter((m) => m.skipped_reason !== null);

    setNote(
      skipped.length === 0
        ? `Loaded ${count(r.rows_loaded, 'row')} across ${count(r.loaded.length, 'module')}.`
        : `${r.rows_loaded > 0 ? `Loaded ${count(r.rows_loaded, 'row')}. ` : ''}` +
          skipped
            .map((m) => `${m.module_code}: ${m.skipped_reason}`)
            .join(' '),
    );
    return r;
  });

  const removeAll = useMutation(async () => {
    const r = await api<SampleStatus & { rows_removed: number; removed: string[] }>(
      '/v1/sample-data',
      { method: 'DELETE' },
    );
    setStatus(r);
    setConfirming(null);
    setNote(`Removed ${count(r.rows_removed, 'row')}. This club has no sample data left.`);
    return r;
  });

  const busy = load.busy || remove.busy || loadAll.busy || removeAll.busy;
  const error = load.error ?? remove.error ?? loadAll.error ?? removeAll.error;

  if (query.loading && !current) return <Loading rows={6} />;

  const modules = current?.modules ?? [];
  const loaded = modules.filter((m) => m.state === 'loaded');

  return (
    <>
        <h1>Sample Data</h1>
        <p className="muted">
          A plausible club, loaded a module at a time, so every screen has something in it
          before this club has records of its own. It is tagged as it goes in, which is what
          lets all of it come back out again.
        </p>

      <ErrorBanner error={query.error ?? error} />
      {note && <div className="notice">{note}</div>}

      <div className="tiles">
        <Tile label="Modules Loaded" value={`${loaded.length} of ${modules.length}`} />
        <Tile label="Sample Rows" value={(current?.total_rows ?? 0).toLocaleString()} />
        <Tile
          label="This Club"
          value={loaded.length === 0 ? 'Its own records only' : 'Holding sample data'}
          note={
            loaded.length === 0 ? undefined : 'Remove it before the club goes live'
          }
          tone={loaded.length === 0 ? undefined : 'warning'}
        />
      </div>

      {mayChange && (
        <Card title="Everything at Once">
          <p className="muted small">
            Loads every module this club is eligible for, in the order they depend on each
            other. A module that cannot be loaded is reported rather than stopping the rest.
          </p>
          <div className="actions">
            <button onClick={() => loadAll.run()} disabled={busy}>
              {loadAll.busy ? 'Loading…' : 'Load all sample data'}
            </button>
            <button
              className="secondary"
              onClick={() => setConfirming('all')}
              disabled={busy || loaded.length === 0}
            >
              Remove all sample data
            </button>
          </div>

          {confirming === 'all' && (
            <Confirm
              word="remove"
              busy={removeAll.busy}
              onConfirm={() => removeAll.run()}
              onCancel={() => setConfirming(null)}
            >
              <p>
                This deletes {count(current?.total_rows ?? 0, 'row')} from{' '}
                {count(loaded.length, 'module')} — members, charges, statements, invoices and
                everything posted behind them.
              </p>
              <p className="muted small">
                Only rows a sample loader created are touched. Anything this club entered
                itself is left exactly where it is, and if any part of the removal fails,
                none of it happens.
              </p>
            </Confirm>
          )}
        </Card>
      )}

      <Card title="By Module" flush>
        <table className="data">
          <thead>
            <tr>
              <th>Module</th>
              <th>What you get</th>
              <th className="num">Rows</th>
              <th>Status</th>
              {mayChange && <th />}
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.code}>
                <td>
                  <strong>{m.name}</strong>
                  <div className="muted small">{m.entitlement_key}</div>
                </td>
                <td className="wrap">
                  {m.description}
                  {m.state === 'blocked' && m.blocked_reason && (
                    <div className="muted small">{m.blocked_reason}</div>
                  )}
                </td>
                <td className="num">{m.state === 'loaded' ? m.row_count.toLocaleString() : '—'}</td>
                <td>
                  {m.state === 'loaded' && (
                    <Status tone="good">Loaded {when(m.loaded_at)}</Status>
                  )}
                  {m.state === 'available' && <Status tone="neutral">Available</Status>}
                  {m.state === 'blocked' && <Status tone="warning">Not offered</Status>}
                </td>
                {mayChange && (
                  <td className="num">
                    {m.state === 'loaded' && (
                      <button
                        className="secondary small"
                        disabled={busy}
                        onClick={() => remove.run(m.code)}
                      >
                        {busyModule === m.code ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                    {m.state === 'available' && (
                      <button className="small" disabled={busy} onClick={() => load.run(m.code)}>
                        {busyModule === m.code ? 'Loading…' : 'Load'}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {!mayChange && (
        <p className="muted small">
          Loading and removing sample data is a club administrator's action. You can see what
          is loaded here.
        </p>
      )}

      <OwnRecords
        mayChange={mayChange}
        blocked={modules.some((m) => m.state === 'blocked')}
        onErased={() => query.reload()}
      />
    </>
  );
}

/**
 * The club's own records, and the way out of the corner.
 *
 * This sits under the module list because that is where the refusal
 * appears: "this club already has 1 real record in member_account" is
 * unhelpful on its own, and the useful next question — *which* record,
 * and what would go if it went — has to be answerable in the same place.
 *
 * Collapsed until asked for. Most clubs never open it, and a screen that
 * leads with "erase everything" is a screen that gets used carelessly.
 */
function OwnRecords({
  mayChange,
  blocked,
  onErased,
}: {
  mayChange: boolean;
  blocked: boolean;
  onErased: () => void;
}) {
  const [open, setOpen] = useState(false);
  const census = useApi<Census>(open ? '/v1/club-records' : null);
  const [scope, setScope] = useState<'members' | 'everything'>('members');
  const [plan, setPlan] = useState<EraseResult | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const preview = useMutation(async (which: 'members' | 'everything') => {
    const r = await api<EraseResult>('/v1/club-records/erase', {
      method: 'POST',
      body: { scope: which },
    });
    setPlan(r);
    setNote(null);
    return r;
  });

  const erase = useMutation(async () => {
    if (!plan?.confirmation) return null;

    // The confirmation carries the row count the preview saw. If anything
    // has changed in this club since — another session, a nightly run —
    // the database refuses it rather than deleting a different set than
    // the one that was shown.
    const r = await api<EraseResult>('/v1/club-records/erase', {
      method: 'POST',
      body: { scope: plan.scope, confirm: plan.confirmation },
    });

    setPlan(null);
    setNote(`Erased ${count(r.rows_deleted, 'record')}. The sample data can load now.`);

    // Both, and in this order. `onErased` refreshes the module list above —
    // which is the point of having pressed this — and the census behind this
    // card is now stale by exactly the number just deleted, so the tiles
    // would go on reporting records that no longer exist.
    census.reload();
    onErased();
    return r;
  });

  if (!open) {
    return (
      <Card title="This Club's Own Records">
        <p className="muted small">
          {blocked
            ? 'Some modules are refusing to load because this club has records of its own. ' +
              'You can see what they are, and remove them if they were only ever a trial run.'
            : 'What this club has entered for itself, as opposed to sample data or its setup.'}
        </p>
        <div className="actions">
          <button className="secondary" onClick={() => setOpen(true)}>
            Show me what's here
          </button>
        </div>
      </Card>
    );
  }

  const rows = census.data?.tables ?? [];

  // Tables that hold rows *of the club's own*. Filtering on `kind` alone was
  // the bug in the first version: every table the sample data had filled
  // showed up here with a zero in the column that mattered, and the screen
  // then offered to erase records that did not exist.
  const records = rows.filter((t) => t.kind === 'records' && Number(t.own_rows) > 0);

  const sampleLoaded = (census.data?.sample_rows ?? 0) > 0;

  return (
    <Card title="This Club's Own Records">
      <ErrorBanner error={census.error ?? preview.error ?? erase.error} />
      {note && <div className="notice">{note}</div>}

      {census.loading && <Loading rows={4} />}

      {census.data && (
        <>
          <div className="tiles">
            <Tile label="Its Own Records" value={census.data.own_records.toLocaleString()} />
            <Tile
              label="Its Setup"
              value={census.data.own_configuration.toLocaleString()}
              note="Accounts, departments, rates — never erased"
            />
            <Tile label="Sample Rows" value={census.data.sample_rows.toLocaleString()} />
            <Tile
              label="Kept Whatever Happens"
              value={rows
                .filter((t) => t.kind === 'protected')
                .reduce((n, t) => n + Number(t.own_rows), 0)
                .toLocaleString()}
              note="Logins, and the audit trail"
            />
          </div>

          {records.length === 0 ? (
            <Empty>
              This club has no records of its own — nothing is standing in the sample data's
              way.
              {sampleLoaded &&
                ' The rows it does hold are sample data, and those come out through' +
                  ' Remove All Sample Data above.'}
            </Empty>
          ) : (
            <>
              <table className="data">
                <thead>
                  <tr>
                    <th>Table</th>
                    <th className="num">This club's own</th>
                    <th className="num">Sample data</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((t) => (
                    <tr key={t.table_name}>
                      <td>{t.table_name}</td>
                      <td className="num">{Number(t.own_rows).toLocaleString()}</td>
                      <td className="num">{Number(t.sample_rows).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {mayChange && (
                <>
                  <p className="muted small" style={{ marginTop: 14 }}>
                    Erasing asks twice. The first press only reports what would go; nothing is
                    deleted until you have seen that list. Three things are never touched: the
                    club's setup — chart of accounts, departments, rate card, course, bylaws —
                    the logins that get you back in, and the record that this happened.
                    {sampleLoaded &&
                      ' Sample data is not touched either: it is not this club\'s record of' +
                        ' anything, and it comes out through Remove All Sample Data above.'}
                  </p>

                  <div className="choices">
                    <label className="inline-choice">
                      <input
                        type="radio"
                        name="erase-scope"
                        checked={scope === 'members'}
                        onChange={() => {
                          setScope('members');
                          setPlan(null);
                        }}
                      />
                      <span>
                        Members and their money
                        <em>
                          Members, what they were charged, what they paid, what they booked.
                          Vendors and invoices stay.
                        </em>
                      </span>
                    </label>
                    <label className="inline-choice">
                      <input
                        type="radio"
                        name="erase-scope"
                        checked={scope === 'everything'}
                        onChange={() => {
                          setScope('everything');
                          setPlan(null);
                        }}
                      />
                      <span>
                        Every record
                        <em>Back to a configured club with nothing in it.</em>
                      </span>
                    </label>
                  </div>

                  <div className="actions">
                    <button
                      className="secondary"
                      onClick={() => preview.run(scope)}
                      disabled={preview.busy || erase.busy}
                    >
                      {preview.busy ? 'Counting…' : 'Show me what would go'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {plan && plan.rows_in_scope === 0 && (
            <div className="notice">Nothing in that scope. There is nothing to erase.</div>
          )}

          {plan && plan.rows_in_scope > 0 && (
            <Confirm
              word="erase"
              busy={erase.busy}
              onConfirm={() => erase.run()}
              onCancel={() => setPlan(null)}
            >
              <p>
                This deletes {count(plan.rows_in_scope, 'record')} from{' '}
                {count(plan.tables.length, 'table')}, permanently:
              </p>
              <ul className="plain">
                {plan.tables.map((t) => (
                  <li key={t.table_name}>
                    {t.table_name} — {Number(t.rows_found).toLocaleString()}
                  </li>
                ))}
              </ul>
              <p className="muted small">
                Members, their charges, their statements and the ledger entries behind them.
                Only rows this club entered itself — nothing a sample loader created, and no
                login. There is no undo and no backup is taken, and if any part of it fails,
                none of it happens.
              </p>
            </Confirm>
          )}
        </>
      )}
    </Card>
  );
}

// =====================================================================
// Export
// =====================================================================

export function ExportDataPage() {
  const query = useApi<Manifest>('/v1/data/manifest');
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const modules = query.data?.modules ?? [];

  // Everything, until the reader says otherwise. A screen that starts
  // with nothing selected makes the common case — "give me all of it" —
  // the one that takes the most clicks.
  const selected = useMemo(
    () => chosen ?? new Set(modules.map((m) => m.code)),
    [chosen, modules],
  );

  const toggle = useCallback(
    (code: string) => {
      const next = new Set(selected);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      setChosen(next);
    },
    [selected],
  );

  const rows = modules
    .filter((m) => selected.has(m.code))
    .reduce((n, m) => n + m.tables.reduce((k, t) => k + t.rows, 0), 0);

  const tables = modules
    .filter((m) => selected.has(m.code))
    .reduce((n, m) => n + m.tables.length, 0);

  async function download() {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      // Not through the api() helper: this answer is a zip, and the
      // helper parses JSON. The filename comes from the server, because
      // the server is the one that knows the club's own local date.
      const res = await fetch('/v1/data/export', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${getToken() ?? ''}`,
        },
        body: JSON.stringify({ modules: [...selected] }),
      });

      if (!res.ok) {
        const envelope = (await res.json().catch(() => null)) as {
          error?: { code: string; message: string };
        } | null;
        throw new ApiError(
          res.status,
          envelope?.error?.code ?? 'export_failed',
          envelope?.error?.message ?? `The export failed (${res.status}).`,
        );
      }

      const name =
        /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
        'betterclub-export.zip';

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);

      setDone(name);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'network', String(e)));
    } finally {
      setBusy(false);
    }
  }

  if (query.loading) return <Loading rows={6} />;

  return (
    <>
        <h1>Export Data</h1>
        <p className="muted">
          One spreadsheet per table, in a zip, with a manifest recording which club it came
          from and when. Amounts stay in minor units — a column called{' '}
          <code>amount_minor</code> holds cents, not dollars — so nothing is rounded on the
          way out.
        </p>

      <ErrorBanner error={query.error ?? error} />
      {done && <div className="notice">Downloaded {done}.</div>}

      <div className="tiles">
        <Tile label="Modules" value={`${selected.size} of ${modules.length}`} />
        <Tile label="Files" value={tables.toLocaleString()} />
        <Tile label="Rows" value={rows.toLocaleString()} />
      </div>

      <Card
        title="What to Include"
        actions={
          <div className="actions">
            <button className="secondary small" onClick={() => setChosen(new Set())}>
              None
            </button>
            <button
              className="secondary small"
              onClick={() => setChosen(new Set(modules.map((m) => m.code)))}
            >
              All
            </button>
          </div>
        }
        flush
      >
        <table className="data">
          <thead>
            <tr>
              <th />
              <th>Module</th>
              <th>Tables</th>
              <th className="num">Rows</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.code}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(m.code)}
                    onChange={() => toggle(m.code)}
                    aria-label={m.name}
                  />
                </td>
                <td>
                  <strong>{m.name}</strong>
                </td>
                <td className="wrap">
                  <div className="muted small">
                    {m.tables.map((t) => t.label).join(' · ')}
                  </div>
                </td>
                <td className="num">
                  {m.tables.reduce((n, t) => n + t.rows, 0).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="actions">
        <button onClick={download} disabled={busy || selected.size === 0}>
          {busy ? 'Building the file…' : `Download ${count(tables, 'file')}`}
        </button>
      </div>

      <p className="muted small">
        Bank routing and account numbers are never included. Every export carries the four
        digits a controller reads out on the telephone and nothing more, because an export is
        not a way around who is allowed to see them.
      </p>
    </>
  );
}

// =====================================================================
// Import
// =====================================================================

export function ImportDataPage() {
  const { atLeast } = useSession();
  const mayApply = atLeast('club_admin');

  const [report, setReport] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const apply = useMutation(async (id: string) => {
    const r = await api<{ id: string; totals: { inserted: number; updated: number; unchanged: number } }>(
      `/v1/data/import/${id}/apply`,
      // `confirm` is the server insisting the client meant it. Sending it
      // from a button the reader had to be shown a report to reach is the
      // whole of what it is asking for.
      { method: 'POST', body: { confirm: true } },
    );
    const fresh = await api<ImportReport>(`/v1/data/import/${id}`);
    setReport(fresh);
    return r;
  });

  const discard = useMutation(async (id: string) => {
    await api(`/v1/data/import/${id}`, { method: 'DELETE' });
    setReport(null);
    if (fileInput.current) fileInput.current.value = '';
  });

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const res = await fetch('/v1/data/import', {
        method: 'POST',
        headers: {
          'content-type': 'application/zip',
          authorization: `Bearer ${getToken() ?? ''}`,
          'x-filename': file.name,
        },
        body: file,
      });

      const payload = (await res.json().catch(() => null)) as
        | ImportReport
        | { error?: { code: string; message: string } }
        | null;

      if (!res.ok) {
        const envelope = payload as { error?: { code: string; message: string } } | null;
        throw new ApiError(
          res.status,
          envelope?.error?.code ?? 'import_failed',
          envelope?.error?.message ?? `That file could not be read (${res.status}).`,
        );
      }

      setReport(payload as ImportReport);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'network', String(e)));
    } finally {
      setBusy(false);
    }
  }

  const conflicts = report?.tables.reduce((n, t) => n + t.conflicts.length, 0) ?? 0;
  const toInsert = report?.tables.reduce((n, t) => n + t.new, 0) ?? 0;
  const toChange = report?.tables.reduce((n, t) => n + t.changed, 0) ?? 0;
  const same = report?.tables.reduce((n, t) => n + t.unchanged, 0) ?? 0;

  return (
    <>
        <h1>Import Data</h1>
        <p className="muted">
          Reading a file changes nothing. The whole of it is checked first and reported back
          — what is new, what would change, and anything that cannot be applied — and only
          then is there a button.
        </p>

      <ErrorBanner error={error ?? apply.error ?? discard.error} />

      <Card title="Choose a File">
        <input
          ref={fileInput}
          type="file"
          accept=".zip,application/zip"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
          }}
        />
        <p className="muted small">
          A zip produced by Export Data, from this club or another one. Rows are matched by
          the things a club actually uses to identify them — an account number, a vendor
          number, an account code — never by an internal id, because an id from somewhere
          else means nothing here.
        </p>
        {busy && <Loading rows={2} />}
      </Card>

      {report && (
        <>
          <div className="tiles">
            <Tile label="New" value={toInsert.toLocaleString()} />
            <Tile label="Changed" value={toChange.toLocaleString()} />
            <Tile label="Unchanged" value={same.toLocaleString()} />
            <Tile
              label="Conflicts"
              value={conflicts.toLocaleString()}
              note={conflicts > 0 ? 'Nothing can be applied until these are fixed' : undefined}
              tone={conflicts > 0 ? 'serious' : undefined}
            />
          </div>

          <Card
            title={report.filename}
            actions={
              <div className="actions">
                {report.status === 'draft' && mayApply && (
                  <button
                    disabled={!report.can_apply || apply.busy}
                    onClick={() => apply.run(report.id)}
                  >
                    {apply.busy ? 'Applying…' : `Apply ${count(toInsert + toChange, 'row')}`}
                  </button>
                )}
                {report.status === 'draft' && (
                  <button
                    className="secondary"
                    onClick={() => discard.run(report.id)}
                    disabled={apply.busy || discard.busy}
                  >
                    Discard
                  </button>
                )}
              </div>
            }
            flush
          >
            {report.status === 'applied' && (
              <div className="notice">
                Applied {when(report.applied_at)}. Nothing further can be done with this file.
              </div>
            )}

            <table className="data">
              <thead>
                <tr>
                  <th>Table</th>
                  <th className="num">In file</th>
                  <th className="num">New</th>
                  <th className="num">Changed</th>
                  <th className="num">Same</th>
                  <th>What would happen</th>
                </tr>
              </thead>
              <tbody>
                {report.tables.map((t) => (
                  <tr key={t.table}>
                    <td>
                      <strong>{t.label}</strong>
                      <div className="muted small">
                        {t.module} · {t.table}
                      </div>
                    </td>
                    <td className="num">{t.rows.toLocaleString()}</td>
                    <td className="num">{t.importable ? t.new.toLocaleString() : '—'}</td>
                    <td className="num">{t.importable ? t.changed.toLocaleString() : '—'}</td>
                    <td className="num">{t.importable ? t.unchanged.toLocaleString() : '—'}</td>
                    <td className="wrap">
                      {!t.importable && <span className="muted small">{t.note ?? 'Read only'}</span>}
                      {t.importable && t.conflicts.length > 0 && (
                        <Status tone="serious">
                          {count(t.conflicts.length, 'conflict')}
                        </Status>
                      )}
                      {t.importable && t.conflicts.length === 0 && t.changed > 0 && (
                        <span className="muted small">
                          Would rewrite {t.changed_columns.join(', ')}
                        </span>
                      )}
                      {t.importable && t.conflicts.length === 0 && t.changed === 0 && t.new > 0 && (
                        <span className="muted small">
                          Would add {count(t.new, 'row')}
                        </span>
                      )}
                      {t.importable && t.conflicts.length === 0 && t.changed === 0 && t.new === 0 && (
                        <span className="muted small">Nothing to do</span>
                      )}
                      {t.conflicts.slice(0, 4).map((c) => (
                        <div key={`${t.table}-${c.row}-${c.reason}`} className="muted small">
                          Row {c.row}: {c.reason}
                        </div>
                      ))}
                      {t.conflicts.length > 4 && (
                        <div className="muted small">
                          …and {t.conflicts.length - 4} more
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {report.unknown_files && report.unknown_files.length > 0 && (
            <Card title="Files This Club Does Not Recognize">
              <p className="muted small">
                Left alone rather than guessed at. They may be from a newer version of
                BetterClub than this one is running.
              </p>
              <ul className="plain">
                {report.unknown_files.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </Card>
          )}

          {!mayApply && report.status === 'draft' && (
            <p className="muted small">
              Applying an import is a club administrator's action. This is what it would do.
            </p>
          )}
        </>
      )}

      {!report && !busy && <Empty>No file loaded.</Empty>}
    </>
  );
}

// =====================================================================
// Demo clubs
// =====================================================================

interface DemoClub {
  id: string;
  slug: string;
  legal_name: string;
  brand_name: string;
  demo_created_at: string | null;
  copied_from: string | null;
  member_count: number;
  sample_rows: number;
}

export function DemoClubsPage() {
  const { atLeast } = useSession();

  // Building a club is a platform action, not a club one, and this
  // session may not be entitled to it. Asking anyway would put a 403 in
  // the console of everybody who opened the page out of curiosity, and a
  // console full of expected errors is a console nobody reads.
  const mayOperate = atLeast('betterclub_support');

  const query = useApi<{ clubs: DemoClub[] }>(mayOperate ? '/v1/demo-clubs' : null);
  const [slug, setSlug] = useState('');
  const [legalName, setLegalName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [dropping, setDropping] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const create = useMutation(async () => {
    const r = await api<{ slug: string }>('/v1/demo-clubs', {
      method: 'POST',
      body: {
        slug: slug.trim(),
        legal_name: legalName.trim(),
        ...(brandName.trim() ? { brand_name: brandName.trim() } : {}),
      },
    });
    setSlug('');
    setLegalName('');
    setBrandName('');
    setNote(`Built ${r.slug}, configured like this club and filled with every module.`);
    query.reload();
    return r;
  });

  const drop = useMutation(async (id: string) => {
    const r = await api<{ sample_rows_removed: number }>(`/v1/demo-clubs/${id}`, {
      method: 'DELETE',
    });
    setDropping(null);
    setNote(`Deleted. ${count(r.sample_rows_removed, 'row')} went with it.`);
    query.reload();
    return r;
  });

  const clubs = query.data?.clubs ?? [];

  return (
    <>
        <h1>Demo Clubs</h1>
        <p className="muted">
          A whole second club, configured like this one — its departments, chart of accounts,
          course and rate card — and filled with every module's sample data. Nothing here
          touches this club's own records.
        </p>

      <ErrorBanner error={query.error ?? create.error ?? drop.error} />
      {note && <div className="notice">{note}</div>}

      {!mayOperate && (
        <Card title="Not Your Account to Do This From">
          <p className="muted small">
            Creating a club makes a whole new tenant, which is a BetterClub operation rather
            than a club one. Ask whoever administers this installation.
          </p>
        </Card>
      )}

      {mayOperate && (
      <Card title="Build One">
        <div className="form-rows">
          <label>
            <span>Address</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="demo-oakbourne"
              spellCheck={false}
            />
          </label>
          <label>
            <span>Legal name</span>
            <input
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="Oakbourne Golf &amp; Country Club"
            />
          </label>
          <label>
            <span>What members call it</span>
            <input
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="The Oaks"
            />
          </label>
        </div>
        <p className="muted small">
          Building one takes a few seconds — thirty-nine configuration tables are copied and
          then every module is loaded, through the same posting engines a real club uses, so
          the demo club's trial balance is a real trial balance.
        </p>
        <div className="actions">
          <button
            onClick={() => create.run()}
            disabled={create.busy || slug.trim() === '' || legalName.trim() === ''}
          >
            {create.busy ? 'Building…' : 'Build a demo club'}
          </button>
        </div>
      </Card>
      )}

      {mayOperate && (
      <Card title="Demo Clubs" flush>
        {clubs.length === 0 ? (
          <div className="card__body">
            <Empty>None yet.</Empty>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Club</th>
                <th>Copied from</th>
                <th>Built</th>
                <th className="num">Members</th>
                <th className="num">Rows</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {clubs.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.legal_name}</strong>
                    <div className="muted small">
                      {c.brand_name} · {c.slug}
                    </div>
                  </td>
                  <td>{c.copied_from ?? '—'}</td>
                  <td>{when(c.demo_created_at)}</td>
                  <td className="num">{Number(c.member_count).toLocaleString()}</td>
                  <td className="num">{Number(c.sample_rows).toLocaleString()}</td>
                  <td className="num">
                    <button
                      className="secondary small"
                      onClick={() => setDropping(c.id)}
                      disabled={drop.busy}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      )}

      {dropping && (
        <Card title="Delete This Demo Club">
          <Confirm
            word="delete"
            busy={drop.busy}
            onConfirm={() => drop.run(dropping)}
            onCancel={() => setDropping(null)}
          >
            <p>
              The club and everything in it goes. This is the only kind of club that can be
              deleted at all — a real club is closed instead, because its records have to
              outlive it.
            </p>
          </Confirm>
        </Card>
      )}
    </>
  );
}

// =====================================================================
// The club's own rules
//
// Migration 0100 made every by-law provision this software enforces into
// a question the club answers rather than a number in a trigger. This is
// where the answering happens, and the reason the whole table exists:
// standing up a second club should be working down a list, not writing a
// migration.
//
// So the unanswered questions lead. A club that has never been asked and
// a club that was asked and said "we have no such rule" behave identically
// today and are completely different conversations, and the only place
// that distinction can be made visible is here.
// =====================================================================

interface Policy {
  code: string;
  label: string;
  group_label: string;
  kind: 'integer' | 'basis_points' | 'money_minor' | 'boolean' | 'choice' | 'text';
  options: string[] | null;
  unit: string | null;
  question: string;
  unset_means: string;
  invariant: string | null;
  answered: boolean;
  value: string | null;
  note: string | null;
  bylaw_section_id: string | null;
  updated_at: string | null;
}

/**
 * Basis points are how the database stores a cap and not how anybody says
 * one. The field shows per cent and converts; the club types 3, not 300.
 */
function toDisplay(p: Policy): string {
  if (p.value === null) return '';
  if (p.kind === 'basis_points') return String(Number(p.value) / 100);
  if (p.kind === 'money_minor') return (Number(p.value) / 100).toFixed(2);
  return p.value;
}

/**
 * What the club reads, as opposed to what the database stores.
 *
 * Booleans are the reason this is separate from `toDisplay`. A by-law
 * answer shown as `false` reads as a bug, and shown as "No" reads as an
 * answer — but the form still needs the literal to round-trip through the
 * select, so the two cannot be the same function.
 */
function toSentence(p: Policy): string {
  if (p.value === null) return '';
  if (p.kind === 'boolean') return p.value === 'true' ? 'Yes' : 'No';
  return `${toDisplay(p)}${unitLabel(p)}`;
}

function toStored(p: Policy, shown: string): string | null {
  const t = shown.trim();
  if (t === '') return null;
  if (p.kind === 'basis_points') return String(Math.round(Number(t) * 100));
  if (p.kind === 'money_minor') return String(Math.round(Number(t) * 100));
  return t;
}

function unitLabel(p: Policy): string {
  if (p.kind === 'basis_points') return '%';
  if (p.kind === 'money_minor') return 'per statement';
  return p.unit ?? '';
}

function PolicyRow({
  policy,
  mayChange,
  onSaved,
}: {
  policy: Policy;
  mayChange: boolean;
  onSaved: (note: string) => void;
}) {
  const [shown, setShown] = useState(() => toDisplay(policy));
  const [note, setNote] = useState(policy.note ?? '');
  const [open, setOpen] = useState(false);

  const save = useMutation(async () => {
    const value = toStored(policy, shown);
    await api(`/v1/settings/policies/${policy.code}`, {
      method: 'PUT',
      body: { value, note: note.trim() || null },
    });
    onSaved(
      value === null
        ? `Recorded: this club has no rule about ${policy.label.toLowerCase()}.`
        : `${policy.label}: ${toSentence({ ...policy, value })}.`,
    );
  });

  const dirty = toStored(policy, shown) !== policy.value || (note.trim() || null) !== policy.note;

  return (
    <div className={`policy${policy.answered ? '' : ' policy--unanswered'}`}>
      <div className="policy__head">
        <div>
          <strong>{policy.label}</strong>{' '}
          {policy.answered ? null : <Status tone="warning">not answered</Status>}
          <div className="muted small">{policy.question}</div>
        </div>
        <button className="secondary small" onClick={() => setOpen((o) => !o)}>
          {open ? 'Close' : policy.answered ? 'Change' : 'Answer'}
        </button>
      </div>

      <div className="policy__now">
        {policy.value === null ? (
          <span className="muted">
            {policy.answered
              ? `No such rule. ${policy.unset_means}`
              : `Nobody has asked. Until they do: ${policy.unset_means.toLowerCase()}`}
          </span>
        ) : (
          <span>
            <strong>{toSentence(policy)}</strong>
            {policy.note ? <span className="muted small"> — {policy.note}</span> : null}
          </span>
        )}
        {policy.invariant ? (
          <span className="muted small policy__code">enforced as {policy.invariant}</span>
        ) : null}
      </div>

      {open && mayChange ? (
        <form
          className="policy__edit"
          onSubmit={(e) => {
            e.preventDefault();
            void save.run();
          }}
        >
          <ErrorBanner error={save.error} />
          <div className="form-rows">
            <label>
              <span>The rule</span>
              {policy.kind === 'boolean' ? (
                <select value={shown} onChange={(e) => setShown(e.target.value)}>
                  <option value="">No such rule</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : policy.kind === 'choice' ? (
                <select value={shown} onChange={(e) => setShown(e.target.value)}>
                  <option value="">No such rule</option>
                  {(policy.options ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={shown}
                  onChange={(e) => setShown(e.target.value)}
                  inputMode={policy.kind === 'text' ? 'text' : 'decimal'}
                  placeholder="leave blank for no such rule"
                />
              )}
              {/* The blank case is the one people get wrong, so it is
                  spelled out rather than implied by an empty box. */}
              <span className="muted small">
                Leave it blank to record that this club adopted no such rule —{' '}
                {policy.unset_means.charAt(0).toLowerCase() + policy.unset_means.slice(1)}
              </span>
            </label>
            <label>
              <span>Where it comes from</span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Article III, Section G — adopted at the 2019 annual meeting"
              />
              <span className="muted small">
                Quoted back to whoever the rule refuses, which is where it gets argued.
              </span>
            </label>
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button type="submit" disabled={save.busy || !dirty}>
              {save.busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

export function ClubRulesPage() {
  const { atLeast } = useSession();
  // A by-law is not a preference. Changing what the software refuses on
  // the club's behalf sits with whoever answers for the club.
  const mayChange = atLeast('controller');

  const query = useApi<{ policies: Policy[]; unanswered: number; note: string }>(
    '/v1/settings/policies',
  );
  const [note, setNote] = useState<string | null>(null);

  const groups = useMemo(() => {
    const out = new Map<string, Policy[]>();
    for (const p of query.data?.policies ?? []) {
      const list = out.get(p.group_label) ?? [];
      list.push(p);
      out.set(p.group_label, list);
    }
    return [...out.entries()];
  }, [query.data]);

  const saved = useCallback(
    (message: string) => {
      setNote(message);
      query.reload();
    },
    [query],
  );

  return (
    <>
      <h1>The Club's Rules</h1>
      <p className="muted" style={{ maxWidth: '62ch', marginBottom: 18 }}>
        Every rule below is one this software will enforce on the club's behalf — refusing
        something, or asking for a vote first. What each one says is the club's by-laws, not
        ours, so nothing here has a default we picked.
      </p>

      <ErrorBanner error={query.error} />
      {note ? <div className="ok-banner">{note}</div> : null}

      {query.loading ? <Loading rows={4} /> : null}

      {query.data && query.data.unanswered > 0 ? (
        <Card>
          <div className="card__body">
            <strong>
              {count(query.data.unanswered, 'question')} nobody has answered for this club yet.
            </strong>
            <div className="muted small" style={{ marginTop: 4 }}>
              Each one behaves as though the club adopted no such rule, which may well be right.
              Answering says so on the record, and stops it being asked again.
            </div>
          </div>
        </Card>
      ) : null}

      {query.data && query.data.policies.length === 0 ? (
        <Empty>
          Nothing here yet. Rules appear as the software learns to enforce them, and each one
          arrives as a question rather than as a number somebody chose.
        </Empty>
      ) : null}

      {groups.map(([group, policies]) => (
        <Card key={group}>
          <div className="card__head">
            <h2>{group}</h2>
          </div>
          <div className="card__body">
            {policies.map((p) => (
              <PolicyRow key={p.code} policy={p} mayChange={mayChange} onSaved={saved} />
            ))}
          </div>
        </Card>
      ))}

      {query.data && !mayChange ? (
        <p className="muted small">
          These are shown but not editable at your role — changing what the software refuses on
          the club's behalf needs the controller role.
        </p>
      ) : null}
    </>
  );
}

// =====================================================================
// Where the club's other rules appear
//
// Migration 0102. The rules page above is what the software enforces;
// this is everything else — thirty-nine of Rockford's forty-two
// provisions — and where the club wants each one shown.
//
// The list is by surface rather than by article, because the question a
// club is answering here is "what should somebody see when they are doing
// X", not "where does Article X belong". A moment with nothing attached
// is worth showing precisely because it is an invitation.
// =====================================================================

interface Surface {
  code: string;
  label: string;
  description: string;
  notices: number;
}

interface NoticeRow {
  id: string;
  surface: string;
  surface_label: string;
  headline: string;
  emphasis: string;
  citation: string;
}

interface Provision {
  kind: 'article' | 'section';
  id: string;
  citation: string;
  heading: string;
  excerpt: string;
}

function NewNotice({
  surface,
  provisions,
  onDone,
}: {
  surface: Surface;
  provisions: Provision[];
  onDone: () => void;
}) {
  const [provisionId, setProvisionId] = useState('');
  const [headline, setHeadline] = useState('');
  const [emphasis, setEmphasis] = useState<'normal' | 'important'>('normal');

  const chosen = provisions.find((p) => p.id === provisionId);

  const save = useMutation(async () => {
    if (!chosen) return;
    await api('/v1/bylaw-notices', {
      method: 'POST',
      body: {
        surface: surface.code,
        // Exactly one of the two, which the database also insists on.
        article_id: chosen.kind === 'article' ? chosen.id : null,
        section_id: chosen.kind === 'section' ? chosen.id : null,
        headline: headline.trim(),
        emphasis,
      },
    });
    onDone();
  });

  return (
    <form
      className="policy__edit"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run();
      }}
    >
      <ErrorBanner error={save.error} />
      <div className="form-rows">
        <label>
          <span>Which rule</span>
          <select value={provisionId} onChange={(e) => setProvisionId(e.target.value)}>
            <option value="">Choose an article or a section…</option>
            {provisions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.citation} — {p.heading}
              </option>
            ))}
          </select>
          {chosen ? <span className="muted small">{chosen.excerpt}…</span> : null}
        </label>

        <label>
          <span>What it should say here</span>
          <input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            maxLength={300}
            placeholder="Sixty days after the Treasurer's notice, membership is forfeited."
          />
          {/* The provision itself is often a paragraph, and a paragraph in
              a banner is a banner nobody reads. */}
          <span className="muted small">
            One line, in the club's words. The full provision is one click away from it.
          </span>
        </label>

        <label className="inline-choice">
          <input
            type="checkbox"
            checked={emphasis === 'important'}
            onChange={(e) => setEmphasis(e.target.checked ? 'important' : 'normal')}
          />
          <span>
            Show it louder — for a rule with a deadline or a consequence attached.
          </span>
        </label>
      </div>
      <div className="actions" style={{ marginTop: 12 }}>
        <button type="submit" disabled={save.busy || !chosen || headline.trim().length < 3}>
          {save.busy ? 'Saving…' : 'Show it here'}
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function BylawNoticesPage() {
  const { atLeast } = useSession();
  const mayChange = atLeast('manager');

  const surfaces = useApi<{ surfaces: Surface[] }>('/v1/bylaw-notices/surfaces');
  const notices = useApi<{ notices: NoticeRow[] }>('/v1/bylaw-notices/all');
  const provisions = useApi<{ provisions: Provision[] }>('/v1/bylaw-notices/provisions');
  const [adding, setAdding] = useState<string | null>(null);

  const reload = useCallback(() => {
    surfaces.reload();
    notices.reload();
    setAdding(null);
  }, [surfaces, notices]);

  const remove = useMutation(async (id: string) => {
    await api(`/v1/bylaw-notices/${id}`, { method: 'DELETE' });
    reload();
  });

  const bySurface = useMemo(() => {
    const out = new Map<string, NoticeRow[]>();
    for (const n of notices.data?.notices ?? []) {
      const list = out.get(n.surface) ?? [];
      list.push(n);
      out.set(n.surface, list);
    }
    return out;
  }, [notices.data]);

  const attached = notices.data?.notices.length ?? 0;

  return (
    <>
      <h1>Where the Club's Rules Appear</h1>
      <p className="muted" style={{ maxWidth: '64ch', marginBottom: 18 }}>
        The page before this one is what the software enforces on the club's behalf. This is
        everything else in the by-laws — the rules it will not refuse, but can put in front of
        whoever they apply to, at the moment they apply.
      </p>

      <ErrorBanner error={surfaces.error ?? notices.error} />
      {surfaces.loading ? <Loading rows={4} /> : null}

      {surfaces.data ? (
        <Card>
          <div className="card__body">
            <strong>
              {attached === 0
                ? 'No rules are being shown anywhere yet.'
                : `${count(attached, 'rule')} shown across the product.`}
            </strong>
            <div className="muted small" style={{ marginTop: 4 }}>
              Nothing here refuses anything. A notice is for the rules the software does not
              enforce — the ones a club would rather see than be stopped by, and the ones no
              software can check.
            </div>
          </div>
        </Card>
      ) : null}

      {(surfaces.data?.surfaces ?? []).map((s) => {
        const list = bySurface.get(s.code) ?? [];
        return (
          <Card key={s.code}>
            <div className="card__head">
              <h2>{s.label}</h2>
              <div className="topbar__spacer" />
              {mayChange ? (
                <button
                  className="secondary small"
                  onClick={() => setAdding(adding === s.code ? null : s.code)}
                >
                  {adding === s.code ? 'Close' : 'Add a rule here'}
                </button>
              ) : null}
            </div>
            <div className="card__body">
              <div className="muted small" style={{ marginBottom: list.length ? 12 : 0 }}>
                {s.description}
              </div>

              {list.map((n) => (
                <div key={n.id} className="policy">
                  <div className="policy__head">
                    <div>
                      <strong>{n.headline}</strong>
                      <div className="muted small">
                        {n.citation}
                        {n.emphasis === 'important' ? ' · shown louder' : ''}
                      </div>
                    </div>
                    {mayChange ? (
                      <button
                        className="secondary small"
                        disabled={remove.busy}
                        onClick={() => void remove.run(n.id)}
                      >
                        Stop showing
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}

              {adding === s.code && provisions.data ? (
                <NewNotice
                  surface={s}
                  provisions={provisions.data.provisions}
                  onDone={reload}
                />
              ) : null}
            </div>
          </Card>
        );
      })}
    </>
  );
}
